import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Sector } from 'recharts';
import { Download, PieChartIcon, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LeadSourceData } from '@/hooks/useAnalytics';

interface LeadSourceChartProps {
  data: LeadSourceData[];
  totalLeads: number;
  onExport?: () => void;
  isLoading?: boolean;
}

interface ActiveShapeProps {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: { source: string; count: number };
  percent: number;
  value: number;
}

const renderActiveShape = (props: ActiveShapeProps) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent } = props;
  
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        className="drop-shadow-lg"
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 12}
        outerRadius={outerRadius + 16}
        fill={fill}
        opacity={0.3}
      />
      <text x={cx} y={cy - 10} textAnchor="middle" fill="hsl(var(--foreground))" className="text-sm font-semibold">
        {payload.source}
      </text>
      <text x={cx} y={cy + 15} textAnchor="middle" fill="hsl(var(--muted-foreground))" className="text-xs">
        {payload.count} leads ({(percent * 100).toFixed(0)}%)
      </text>
    </g>
  );
};

export function LeadSourceChart({
  data,
  totalLeads,
  onExport,
  isLoading = false,
}: LeadSourceChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const onPieEnter = (_: unknown, index: number) => {
    setActiveIndex(index);
    setHoveredIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
    setHoveredIndex(null);
  };

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: LeadSourceData }> }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border rounded-xl p-4 shadow-xl"
        >
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="font-semibold text-foreground">{item.source}</span>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Leads:</span>
              <span className="font-medium text-foreground">{item.count}</span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">Percentage:</span>
              <span className="font-medium text-foreground">{item.percentage}%</span>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  return (
    <Card className="overflow-hidden h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-[#2B7FFF]" />
            Lead Sources
          </CardTitle>
          <CardDescription>Breakdown by acquisition channel</CardDescription>
        </div>
        <Button variant="outline" size="icon" onClick={onExport} className="h-9 w-9">
          <Download className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Total Leads Summary */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center justify-center p-4 rounded-xl bg-gradient-to-br from-[#2B7FFF]/5 to-[#2B7FFF]/10 border border-[#2B7FFF]/20"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#2B7FFF]/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-[#2B7FFF]" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Leads</p>
              <p className="text-2xl font-bold text-foreground">
                {isLoading ? (
                  <span className="inline-block w-16 h-8 bg-muted rounded animate-pulse" />
                ) : (
                  totalLeads.toLocaleString()
                )}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Donut Chart */}
        <div className="h-[250px] relative">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7FFF]" />
            </div>
          ) : data.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
              <PieChartIcon className="h-12 w-12 mb-3 opacity-20" />
              <p className="text-sm">Lead sources not shown</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  activeIndex={activeIndex ?? undefined}
                  activeShape={renderActiveShape}
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="count"
                  onMouseEnter={onPieEnter}
                  onMouseLeave={onPieLeave}
                  animationBegin={0}
                  animationDuration={1200}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="hsl(var(--card))"
                      strokeWidth={3}
                      opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.5}
                      className="transition-all duration-200"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
          
          {/* Center Label */}
          {!isLoading && activeIndex === null && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Top Source</p>
                <p className="text-lg font-bold text-foreground">{data[0]?.source}</p>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
          {data.map((item, index) => (
            <motion.div
              key={item.source}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.05 }}
              className={cn(
                'flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-all',
                'hover:bg-muted',
                hoveredIndex === index && 'bg-muted ring-1 ring-offset-1',
                hoveredIndex !== null && hoveredIndex !== index && 'opacity-50'
              )}
              style={{ 
                ringColor: hoveredIndex === index ? item.color : undefined 
              } as React.CSSProperties}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm font-medium text-foreground">{item.source}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-foreground">{item.count}</span>
                <div className="w-16 bg-muted rounded-full h-1.5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.percentage}%` }}
                    transition={{ delay: 0.5 + index * 0.05, duration: 0.8, ease: 'easeOut' }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-10 text-right">{item.percentage}%</span>
              </div>
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default LeadSourceChart;
